// src/Dashboard/pages/teacher/AttendanceTable/components/AttendanceChartsPanel.jsx

import React, { useEffect, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend as RechartsLegend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getAllAttendances } from "../../../../../services/attendanceServices";
import { formatMonthShort } from "../utils/attendanceTableUtils";

function generateSeasonMonthsSepToJun(season) {
  if (!season || !/^\d{4}-\d{4}$/.test(season)) return [];

  const [startYear, endYear] = season.split("-").map(Number);
  const months = [];

  for (let month = 9; month <= 12; month += 1) {
    months.push(`${startYear}-${String(month).padStart(2, "0")}`);
  }

  for (let month = 1; month <= 6; month += 1) {
    months.push(`${endYear}-${String(month).padStart(2, "0")}`);
  }

  return months;
}

function buildMonthlySeries(season, attendances) {
  const months = generateSeasonMonthsSepToJun(season);

  const map = new Map(
    months.map((month) => [month, { present: 0, absent: 0 }]),
  );

  attendances.forEach((attendance) => {
    const month = String(attendance.date_presence).slice(0, 7);

    if (!map.has(month)) return;

    if (attendance.presence === true) {
      map.get(month).present += 1;
    } else if (attendance.presence === false) {
      map.get(month).absent += 1;
    }
  });

  return months.map((month) => {
    const item = map.get(month);
    const total = item.present + item.absent;
    const rate = total > 0 ? Math.round((item.present / total) * 100) : 0;

    return {
      month,
      label: formatMonthShort(month),
      present: item.present,
      absent: item.absent,
      total,
      rate,
    };
  });
}

export default function AttendanceChartsPanel({
  seasonActive,
  selectedGroup,
  adherents = [],
  allAttendances,
  setAllAttendances,
}) {
  useEffect(() => {
    const load = async () => {
      if (allAttendances && allAttendances.length) return;

      try {
        const data = await getAllAttendances();
        setAllAttendances?.(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur chargement graphiques présences :", error);
      }
    };

    load();
  }, [allAttendances, setAllAttendances]);

  const adherentIdSet = useMemo(
    () => new Set((adherents || []).map((adherent) => Number(adherent.id))),
    [adherents],
  );

  const seasonAttendances = useMemo(() => {
    if (!seasonActive || !allAttendances || !adherentIdSet.size) return [];

    const monthSet = new Set(generateSeasonMonthsSepToJun(seasonActive));

    return (allAttendances || []).filter((attendance) => {
      const month = String(attendance.date_presence).slice(0, 7);

      return (
        adherentIdSet.has(Number(attendance.adherent_id)) && monthSet.has(month)
      );
    });
  }, [seasonActive, allAttendances, adherentIdSet]);

  const chartData = useMemo(
    () => buildMonthlySeries(seasonActive, seasonAttendances),
    [seasonActive, seasonAttendances],
  );

  const totals = useMemo(() => {
    const present = chartData.reduce((sum, item) => sum + item.present, 0);
    const absent = chartData.reduce((sum, item) => sum + item.absent, 0);
    const total = present + absent;
    const rate = total > 0 ? Math.round((present / total) * 100) : 0;

    return { present, absent, total, rate };
  }, [chartData]);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Graphiques
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Saison {seasonActive || "—"} · {selectedGroup?.nom || "Groupe"}
            </h2>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Statistiques de présence sur la saison sportive.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-xs font-bold sm:min-w-[320px]">
            <div className="rounded-2xl bg-emerald-50 px-3 py-2 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60">
              {totals.present}
              <br />
              Prés.
            </div>

            <div className="rounded-2xl bg-rose-50 px-3 py-2 text-rose-700 ring-1 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60">
              {totals.absent}
              <br />
              Abs.
            </div>

            <div className="rounded-2xl bg-indigo-50 px-3 py-2 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60">
              {totals.rate}%
              <br />
              Taux
            </div>
          </div>
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
              Présences / absences par mois
            </h3>

            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#CBD5E1"
                    opacity={0.55}
                  />

                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#64748B", fontSize: 12 }}
                    axisLine={{ stroke: "#CBD5E1" }}
                    tickLine={false}
                  />

                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "#64748B", fontSize: 12 }}
                    axisLine={{ stroke: "#CBD5E1" }}
                    tickLine={false}
                  />
                  <Tooltip />
                  <RechartsLegend />
                  <Bar
                    dataKey="present"
                    name="Présents"
                    stackId="a"
                    fill="#10B981"
                    fillOpacity={0.9}
                    // radius={[6, 6, 0, 0]}
                  />

                  <Bar
                    dataKey="absent"
                    name="Absents"
                    stackId="a"
                    fill="#F43F5E"
                    fillOpacity={0.9}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
            <h3 className="text-sm font-bold text-slate-900 dark:text-white">
              Taux de présence
            </h3>

            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <RechartsLegend />
                  <Line
                    type="monotone"
                    dataKey="rate"
                    name="Taux (%)"
                    stroke="#013E8E"
                    strokeWidth={3}
                    dot={{
                      r: 4,
                      fill: "#013E8E",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 6,
                      fill: "#4CA82F",
                      stroke: "#ffffff",
                      strokeWidth: 2,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Les graphiques utilisent la période septembre → juin.
        </p>
      </div>
    </section>
  );
}
